import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { CheckCircle2, Loader2, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import SectionHeader from "@/components/SectionHeader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  name: "",
  phone: "",
  email: "",
  property_type: "",
  bedrooms: "",
  bathrooms: "",
  cleaning_type: "",
  preferred_date: "",
  time_slot: "",
  details: "",
};

const TIME_SLOTS = ["Morning · 8–11 AM", "Midday · 11 AM–2 PM", "Afternoon · 2–5 PM"];

const inputCls =
  "rounded-xl border-[#8E7CC3]/25 bg-white focus-visible:ring-[#8E7CC3] h-12";

const QuoteForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [dateObj, setDateObj] = useState(undefined);
  const [availability, setAvailability] = useState({ blocked_dates: [], booked: [] });

  const loadAvailability = () => {
    axios
      .get(`${API}/availability`)
      .then((r) => setAvailability(r.data))
      .catch(() => {});
  };

  useEffect(loadAvailability, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target ? e.target.value : e }));

  const pickDate = (d) => {
    setDateObj(d);
    setForm((f) => ({ ...f, preferred_date: d ? format(d, "yyyy-MM-dd") : "", time_slot: "" }));
  };

  const pickSlot = (slot) => setForm((f) => ({ ...f, time_slot: slot }));

  const isSlotTaken = (slot) =>
    availability.booked.some((b) => b.date === form.preferred_date && b.slot === slot);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setDone(true);
      toast.success("Thank you! Glow & Shine Sisters will contact you soon.");
    } catch (err) {
      if (err.response?.status === 409) {
        toast.error(err.response.data.detail);
        loadAvailability();
      } else {
        toast.error("Something went wrong. Please call us at (856) 308-0562.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" className="bg-[#FAF8FF] py-20 sm:py-24" data-testid="quote-section">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          align="center"
          eyebrow="Free, No Obligation"
          title="Request Your Free Quote"
          subtitle="Tell us about your space and we will get back to you with an honest, accurate quote."
        />

        <div className="mt-12 rounded-3xl border border-[#8E7CC3]/15 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.06)] sm:p-10">
          {done ? (
            <div className="py-10 text-center" data-testid="quote-success-message">
              <CheckCircle2 className="mx-auto h-14 w-14 text-[#8E7CC3]" aria-hidden="true" />
              <h3 className="mt-6 text-2xl font-medium text-[#2C2A35]">Thank you!</h3>
              <p className="mt-3 text-base text-[#6B6776]">
                Glow &amp; Shine Sisters will contact you soon.
              </p>
              <button
                onClick={() => { setForm(EMPTY); setDateObj(undefined); setDone(false); }}
                data-testid="quote-another-button"
                className="mt-8 text-sm font-bold uppercase tracking-wider text-[#8E7CC3] underline-offset-4 hover:underline"
              >
                Request another quote
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="grid gap-6 sm:grid-cols-2" data-testid="quote-form">
              <div>
                <Label htmlFor="q-name">Full Name *</Label>
                <Input id="q-name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Jane Smith" data-testid="quote-name-input" />
              </div>
              <div>
                <Label htmlFor="q-phone">Phone Number *</Label>
                <Input id="q-phone" required type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(856) 555-0123" data-testid="quote-phone-input" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="q-email">Email *</Label>
                <Input id="q-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.com" data-testid="quote-email-input" />
              </div>
              <div>
                <Label>Property Type *</Label>
                <Select required value={form.property_type} onValueChange={set("property_type")}>
                  <SelectTrigger className={inputCls} data-testid="quote-property-type-select" aria-label="Property Type">
                    <SelectValue placeholder="Select property type" />
                  </SelectTrigger>
                  <SelectContent>
                    {["House", "Apartment", "Office", "School", "Other"].map((o) => (
                      <SelectItem key={o} value={o} data-testid={`quote-property-${o.toLowerCase()}`}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cleaning Type *</Label>
                <Select required value={form.cleaning_type} onValueChange={set("cleaning_type")}>
                  <SelectTrigger className={inputCls} data-testid="quote-cleaning-type-select" aria-label="Cleaning Type">
                    <SelectValue placeholder="Select cleaning type" />
                  </SelectTrigger>
                  <SelectContent>
                    {["Regular", "Deep Cleaning", "Move-In / Move-Out", "Recurring", "Commercial"].map((o) => (
                      <SelectItem key={o} value={o}>{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="q-bedrooms">Number of Bedrooms</Label>
                <Input id="q-bedrooms" type="number" min="0" max="20" value={form.bedrooms} onChange={set("bedrooms")} className={inputCls} placeholder="e.g. 3" data-testid="quote-bedrooms-input" />
              </div>
              <div>
                <Label htmlFor="q-bathrooms">Number of Bathrooms</Label>
                <Input id="q-bathrooms" type="number" min="0" max="20" value={form.bathrooms} onChange={set("bathrooms")} className={inputCls} placeholder="e.g. 2" data-testid="quote-bathrooms-input" />
              </div>
              <div className="sm:col-span-2">
                <Label>Preferred Date &amp; Time Slot</Label>
                <div className="mt-2 grid gap-6 rounded-2xl border border-[#8E7CC3]/20 bg-[#FAF8FF] p-5 sm:grid-cols-2">
                  <div className="flex justify-center" data-testid="quote-calendar">
                    <Calendar
                      mode="single"
                      selected={dateObj}
                      onSelect={pickDate}
                      disabled={(d) =>
                        d < new Date(new Date().setHours(0, 0, 0, 0)) ||
                        availability.blocked_dates.includes(format(d, "yyyy-MM-dd"))
                      }
                      className="rounded-xl bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                    />
                  </div>
                  <div>
                    <p className="flex items-center gap-2 text-sm font-semibold text-[#2C2A35]">
                      <Clock className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" />
                      {dateObj ? format(dateObj, "EEEE, MMMM d") : "Pick a date first"}
                    </p>
                    <div className="mt-4 space-y-3">
                      {TIME_SLOTS.map((slot, i) => {
                        const taken = form.preferred_date && isSlotTaken(slot);
                        const active = form.time_slot === slot;
                        return (
                          <button
                            key={slot}
                            type="button"
                            disabled={!dateObj || taken}
                            onClick={() => pickSlot(slot)}
                            data-testid={`quote-slot-${i}`}
                            className={`w-full rounded-full border px-5 py-3 text-left text-sm font-semibold transition-all duration-200 ${
                              taken
                                ? "cursor-not-allowed border-[#8E7CC3]/10 bg-[#F9F9F9] text-[#6B6776]/50 line-through"
                                : active
                                  ? "border-[#8E7CC3] bg-[#8E7CC3] text-white shadow-[0_8px_30px_rgb(142,124,195,0.35)]"
                                  : "border-[#8E7CC3]/30 bg-white text-[#2C2A35] hover:border-[#8E7CC3] hover:bg-[#E6E6FA]/50"
                            }`}
                          >
                            {slot}
                            {taken && <span className="ml-2 text-xs no-underline">— booked</span>}
                          </button>
                        );
                      })}
                    </div>
                    <p className="mt-4 text-xs leading-relaxed text-[#6B6776]">
                      Grayed-out days are unavailable. Slots already requested show as booked.
                    </p>
                  </div>
                </div>
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="q-details">Additional Details</Label>
                <Textarea id="q-details" rows={4} value={form.details} onChange={set("details")} className="rounded-xl border-[#8E7CC3]/25 bg-white focus-visible:ring-[#8E7CC3]" placeholder="Pets, special requests, areas needing extra attention..." data-testid="quote-details-input" />
              </div>
              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="quote-submit-button"
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-[#8E7CC3] px-8 py-4 text-sm font-bold uppercase tracking-wider text-white shadow-[0_8px_30px_rgb(142,124,195,0.35)] transition-transform hover:scale-[1.01] disabled:opacity-60"
                >
                  {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                  {loading ? "Sending..." : "Request My Free Quote"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default QuoteForm;
